import { useMemo, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Copy,
  ExternalLink,
  FileText,
  PanelsTopLeft,
  Radio,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { CitizenCard } from '../components/CitizenCard';
import { EclesiarMark } from '../components/EclesiarMark';
import { DEMO_CITIZEN } from '../data/demoCitizen';

const REFERRAL_URL = 'https://app.eclesiar.com/?referal_id=1842&recruit_country_id=31&utm_source=twitch&utm_medium=description&utm_campaign=eclesiar_id';

export function LabPage() {
  const [copied, setCopied] = useState<'copy' | 'url' | null>(null);
  const descriptionCopy = useMemo(
    () => `I serve Portugal in Eclesiar as Aurelius Vale. Build, trade, and fight alongside our citizens. Join through my recruitment link: ${REFERRAL_URL}`,
    [],
  );

  async function copyValue(kind: 'copy' | 'url', value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 1600);
  }

  return (
    <main className="lab-page">
      <nav className="lab-nav">
        <a className="lab-brand" href="#top" aria-label="Eclesiar ID home">
          <span><EclesiarMark /></span>
          <strong>Eclesiar <i>ID</i></strong>
        </a>
        <div className="lab-nav__status"><span /> MVP · Panel first</div>
        <div className="lab-nav__links">
          <a href="#product">Product</a>
          <a href="#recruitment">Recruitment kit</a>
          <a className="lab-nav__button" href="config.html">Open setup <ArrowRight size={14} /></a>
        </div>
      </nav>

      <section className="lab-hero" id="top">
        <div className="lab-hero__copy">
          <p className="eyebrow"><ShieldCheck size={15} /> Official citizen record · built for Twitch</p>
          <h1>Your Eclesiar character, <em>verified</em> for the stream.</h1>
          <p className="lab-hero__lede">
            A compact civic credential that turns a streamer’s game identity into something viewers can inspect—country, ranks, affiliations, and service record.
          </p>
          <div className="lab-hero__actions">
            <a className="hero-action" href="panel.html">View the panel <ExternalLink size={15} /></a>
            <a className="hero-action hero-action--quiet" href="#recruitment">See recruitment flow</a>
          </div>
          <div className="lab-proof">
            <span><BadgeCheck size={15} /> One-time account claim</span>
            <span><ShieldCheck size={15} /> Sanitized public data</span>
            <span><Radio size={15} /> Live registry sync</span>
          </div>
        </div>

        <div className="lab-hero__artifact">
          <div className="artifact-note artifact-note--top"><span>01</span> Twitch panel · 318 × 496</div>
          <div className="panel-mount">
            <CitizenCard data={DEMO_CITIZEN} previewLabel="Interactive demo" />
          </div>
          <div className="artifact-note artifact-note--bottom"><span /> Select “Service record” on the credential</div>
        </div>
      </section>

      <section className="product-strip" id="product">
        <div className="product-strip__intro">
          <p className="eyebrow">The first release</p>
          <h2>One identity, two jobs.</h2>
          <p>The extension earns attention with a useful character profile. Recruitment stays in the streamer’s native About description, where Twitch permits it.</p>
        </div>
        <div className="product-path">
          <article>
            <span className="product-path__number">01</span>
            <span className="product-path__icon"><PanelsTopLeft /></span>
            <h3>Citizen ID panel</h3>
            <p>Identity and service tabs make the panel a genuine viewer experience—not a static advertisement.</p>
            <small><Check size={13} /> Twitch submission surface</small>
          </article>
          <span className="product-path__connector"><ArrowRight /></span>
          <article>
            <span className="product-path__number">02</span>
            <span className="product-path__icon"><FileText /></span>
            <h3>Recruitment description</h3>
            <p>A country-aware referral link and ready-to-paste copy live in the streamer’s normal Twitch About area.</p>
            <small><Check size={13} /> Keeps referral attribution</small>
          </article>
          <span className="product-path__connector"><ArrowRight /></span>
          <article>
            <span className="product-path__number">03</span>
            <span className="product-path__icon"><Sparkles /></span>
            <h3>Live component later</h3>
            <p>War, election, or construction state can become an on-video component when it is truly stream-relevant.</p>
            <small>Phase two · after panel approval</small>
          </article>
        </div>
      </section>

      <section className="recruitment-section" id="recruitment">
        <div className="recruitment-copy">
          <p className="eyebrow"><FileText size={14} /> Native Twitch description kit</p>
          <h2>Recruit for your country—outside the extension frame.</h2>
          <p>
            The URL keeps the existing Eclesiar <code>referal_id</code>, adds a country hint for onboarding, and marks Twitch as the source. Country remains a player choice; the hint only preselects it.
          </p>
          <div className="policy-note">
            <ShieldCheck size={17} />
            <span><strong>Review-safe separation</strong>The submitted panel contains no signup CTA or disguised referral link.</span>
          </div>
        </div>
        <div className="copy-kit">
          <div className="copy-kit__header">
            <span><EclesiarMark /></span>
            <div><small>Streamer asset</small><strong>About description</strong></div>
            <span className="copy-kit__country">PT · Portugal</span>
          </div>
          <label htmlFor="description-copy">Suggested copy</label>
          <div className="copy-field copy-field--multiline">
            <textarea id="description-copy" value={descriptionCopy} readOnly />
            <button type="button" onClick={() => void copyValue('copy', descriptionCopy)}>
              {copied === 'copy' ? <Check size={15} /> : <Copy size={15} />}
              {copied === 'copy' ? 'Copied' : 'Copy text'}
            </button>
          </div>
          <label htmlFor="referral-url">Attributed recruitment URL</label>
          <div className="copy-field">
            <input id="referral-url" value={REFERRAL_URL} readOnly />
            <button type="button" onClick={() => void copyValue('url', REFERRAL_URL)} aria-label="Copy recruitment URL">
              {copied === 'url' ? <Check size={15} /> : <Copy size={15} />}
            </button>
          </div>
          <p className="copy-kit__hint">Production copy is generated from the signed-in citizen and current country.</p>
        </div>
      </section>

      <footer className="lab-footer">
        <div className="lab-brand"><span><EclesiarMark /></span><strong>Eclesiar <i>ID</i></strong></div>
        <p>Citizen identity for the Eclesiar world.</p>
        <div>
          <a href="panel.html">Panel</a>
          <a href="config.html">Configuration</a>
          <a href="privacy.html">Privacy</a>
          <a href="terms.html">Terms</a>
          <a href="support.html">Support</a>
        </div>
      </footer>
    </main>
  );
}
